/**
 * Barstool seating for islands and peninsulas.
 *
 * A seated counter edge differs from an ordinary one in two ways:
 *
 *   1. The countertop cantilevers `KITCHEN_SEATING_OVERHANG` (12") past
 *      the cabinet face instead of the usual 1" reveal, so a diner's
 *      knees clear the cabinet body.
 *   2. Stools stand in front of it, one per `BARSTOOL_SEAT_SPACING`
 *      (24") of edge.
 *
 * Seating an edge is gated on TWO different rectangles, and keeping
 * them apart is what makes the feature both safe and possible:
 *
 *   - **The clearance zone** (`seatingZoneCorners`) — `KITCHEN_SEATING_CLEARANCE`
 *     (36") deep starting at the counter LIP. The "is there room for a
 *     person here" test: it must be inside the floor and free of
 *     cabinets. That 36" does double duty as NKBA's clearance behind a
 *     seated diner and as this codebase's walkway floor
 *     (`ISLAND_AISLE_MIN` / `KITCHEN_MIN_WALKWAY`).
 *   - **The seating strip** (`seatingStripCorners`) — everything the
 *     seating physically OCCUPIES: the 12" counter cantilever plus the
 *     stools standing under it, `SEATING_STRIP_DEPTH` (23") from the
 *     cabinet face. This is what must not overlap a walkway corridor.
 *
 * Testing the *strip* against corridors rather than the *zone* is the
 * whole trick. An earlier version vetoed any edge whose 36" zone a
 * corridor crossed; the generator's walkway MST fills an open kitchen,
 * so one crosses essentially every aisle and **no fixture kitchen could
 * seat anyone**. The strip is the part actually in the way, so gating on
 * it keeps counters and stools out of the purple paths without demanding
 * that the whole diner's envelope be corridor-free.
 *
 * Pure geometry — no I/O, no object-graph knowledge. Callers supply the
 * edge and the free-space context they have:
 *
 *   - islands measure free depth directly (`islandPlacement` already
 *     computes the aisle on each side) and call `seatingFitsInDepth`.
 *   - peninsulas have no such measurement and call `probeSeatingZone`,
 *     which tests the zone rectangle against a polygon + obstacles.
 *
 * Both then call `stripBlocksWalkway` with the layout's corridors.
 *
 * NOTE for islands: the MST routes its path hard against the island
 * face (placement rejects islands overlapping a corridor), so an island's
 * strip usually lands in a corridor and it is the 59" figure that binds,
 * not the 48" one. A shallower island does not help — the corridor
 * follows it in. See reference-docs/kitchen-layout/materialization.md.
 */

const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
  KITCHEN_COUNTERTOP_FRONT_OVERHANG,
  KITCHEN_SEATING_OVERHANG,
  KITCHEN_SEATING_CLEARANCE,
  KITCHEN_MIN_WALKWAY,
  BARSTOOL_SEAT_SPACING,
  BARSTOOL_MIN_SEATS,
  BARSTOOL_DIMENSIONS,
} = require('./constants');
// The island body owns the seated/storage depth split; the gate below
// only needs to know when the knee is carved inside the footprint.
const { carvesSeatingKnee } = require('../cabinet-runs/freestanding');
const {
  calculateCorners,
  doPolygonsOverlap,
  isPointInsidePolygon,
} = require('../bathroom-analyzer/pipeline/geometry');
const { footprintFor } = require('../geometry/objectFootprint');

/**
 * Total free depth a seated edge needs, measured from the CABINET FACE:
 * the knee overhang plus the diner's clearance beyond the lip.
 */
const SEATING_DEPTH_REQUIRED =
  KITCHEN_SEATING_OVERHANG + KITCHEN_SEATING_CLEARANCE;

/**
 * How far the stool's nose tucks under the counter lip. Half the
 * overhang leaves the seat readably under the slab without the backrest
 * fouling it — derived from the overhang rather than tuned separately so
 * the two can't drift.
 */
const STOOL_TUCK_UNDER = KITCHEN_SEATING_OVERHANG / 2;

/**
 * Depth of the strip the seating physically occupies, from the cabinet
 * face: the knee cantilever, then the stool standing under it (its nose
 * tucked `STOOL_TUCK_UNDER` back under the lip). Derived, not tuned —
 * change the overhang or the stool and this follows.
 */
const SEATING_STRIP_DEPTH =
  KITCHEN_SEATING_OVERHANG - STOOL_TUCK_UNDER + BARSTOOL_DIMENSIONS.depth;

/**
 * Free depth a seated edge needs when a walkway runs ALONG it: the strip
 * the seating occupies, plus a full `KITCHEN_MIN_WALKWAY` behind it so
 * someone can still get past the stools. The path is free to shift
 * outward — what must survive is the circulation, not the particular
 * rectangle the MST happened to draw.
 */
const SEATING_PLUS_WALKWAY_DEPTH = SEATING_STRIP_DEPTH + KITCHEN_MIN_WALKWAY;

const PARALLEL_COS = Math.cos((30 * Math.PI) / 180);

/**
 * The circulation gate for a seated edge. Replaces the old "the strip
 * must not touch a corridor" veto, which rejected islands carrying 61"
 * and 73" of free aisle purely because the MST had drawn its path next
 * to them — the aisle was ample, the rectangle was just in the way.
 *
 * What matters is that circulation SURVIVES, not that the drawn
 * rectangle is untouched: the corridor is one instantiation of a path,
 * free to re-route. So an edge whose strip lands in a corridor must
 * carry `SEATING_PLUS_WALKWAY_DEPTH` — the stools plus a full walkway
 * behind them — while an edge clear of every corridor only needs the
 * NKBA seated-diner clearance.
 *
 * Deliberately depth-only. An earlier version also asked whether the
 * corridor ran ALONG the edge (shiftable) or ACROSS it (not), but the
 * MST emits 24–27 short rectangles per variant and a single rect's
 * aspect ratio is a poor proxy for the path's direction — it classified
 * every Montrose island as "crossing". At 59" there is room to get past
 * the stools whichever way the path runs.
 *
 * @param {Object} args
 * @param {Object} args.edge - As `seatingStripCorners`.
 * @param {number} args.freeDepth - Clear depth from the cabinet face.
 * @param {Array<Array<{x,z}>>} [args.corridorRectangles=[]]
 * @returns {boolean}
 */
/**
 * Free depth a seated edge owes the diner, given the island's own depth.
 *
 * When the knee space is carved out of the island (deep enough that
 * `frontBackDepths` can reserve it — `carvesSeatingKnee`), the
 * counter no longer reaches into the aisle, so the floor beyond the
 * island owes only the diner's own KITCHEN_SEATING_CLEARANCE. A
 * shallower island still cantilevers its overhang outward and owes
 * both.
 *
 * This is the arithmetic behind "islands rarely seat anyone": with the
 * overhang outside, the requirement is 48", and on the measured rooms
 * the seating side offered a median 19". At 36" three times as many
 * placements qualify.
 */
function seatedIslandFreeDepthNeeded(islandDepth) {
  return Number.isFinite(islandDepth) && carvesSeatingKnee(islandDepth)
    ? KITCHEN_SEATING_CLEARANCE
    : SEATING_DEPTH_REQUIRED;
}

function seatingCirculationOk({
  edge,
  freeDepth,
  corridorRectangles = [],
  islandDepth,
}) {
  if (!Number.isFinite(freeDepth)) return false;
  const needed = seatedIslandFreeDepthNeeded(islandDepth);
  const inPath = stripBlocksWalkway(edge, corridorRectangles);
  // Crossing a walkway is not a veto — it demands a full walkway BEHIND
  // the stools on top of whatever the seating itself needs.
  return freeDepth >= (inPath ? needed + KITCHEN_MIN_WALKWAY : needed);
}

/** True when `freeDepth` (from the cabinet face outward) can seat diners. */
function seatingFitsInDepth(freeDepth) {
  return Number.isFinite(freeDepth) && freeDepth >= SEATING_DEPTH_REQUIRED;
}

/**
 * How many stools an edge of `length` carries. NKBA allots 24" of
 * counter per counter-height seat. This is the raw capacity — see
 * `edgeSeatsEnough` for whether that capacity is worth seating.
 *
 * The epsilon is load-bearing, not tidiness. An edge is a difference of
 * wall-params built out of inch constants, so an edge of exactly N seats'
 * worth lands a few ULP either side of the boundary: a 48" peninsula edge
 * measured 1.2191999999999996 against a 0.6095999999999999 spacing, giving
 * 1.9999999999999996, which `Math.floor` takes to ONE seat. The leg then
 * failed the two-seat minimum and went unseated — 72 of one arm's rejections
 * at 50 Park St, its single largest cause. A millimetre is far below any real
 * seat and far above the drift.
 */
const SEAT_SPACING_EPS = 0.001;
function seatCountForEdge(length) {
  if (!(length > 0)) return 0;
  return Math.floor((length + SEAT_SPACING_EPS) / BARSTOOL_SEAT_SPACING);
}

/**
 * Whether an edge is worth seating at all: it must hold at least
 * `BARSTOOL_MIN_SEATS`. An edge below that keeps its plain 1" reveal and
 * gets neither overhang nor stools. Every gate uses this, including
 * `buildBarstoolObjects`, so a single-stool run cannot be emitted from
 * any path.
 */
function edgeSeatsEnough(length) {
  return seatCountForEdge(length) >= BARSTOOL_MIN_SEATS;
}

/**
 * Footprint of the seating zone — the strip a diner occupies, starting
 * at the counter lip and running `KITCHEN_SEATING_CLEARANCE` outward.
 *
 * @param {Object} edge
 * @param {{x,z}} edge.faceCenter - Midpoint of the CABINET face (not the lip).
 * @param {{x,z}} edge.outward - Unit vector from the counter toward the seats.
 * @param {{x,z}} edge.along - Unit vector along the edge.
 * @param {number} edge.length - Edge length.
 * @returns {Array<{x,z}>} Four corners, `calculateCorners` convention.
 */
function bandCorners({ faceCenter, outward, along, length }, from, to) {
  const offset = (from + to) / 2;
  const center = {
    x: faceCenter.x + offset * outward.x,
    z: faceCenter.z + offset * outward.z,
  };
  const ry = Math.atan2(along.z, along.x);
  return calculateCorners(center, length, to - from, ry);
}

/**
 * The band a seated edge must keep clear, given whether a walkway runs
 * through it. Clear of a corridor it is the NKBA seated-diner zone
 * (lip → 48"); inside one it is the walkway that must survive behind
 * the stools (23" → 59"). See `seatingCirculationOk`.
 */
function requiredClearBand(inPath) {
  return inPath
    ? { from: SEATING_STRIP_DEPTH, to: SEATING_PLUS_WALKWAY_DEPTH }
    : { from: KITCHEN_SEATING_OVERHANG, to: SEATING_DEPTH_REQUIRED };
}

function seatingZoneCorners({ faceCenter, outward, along, length }) {
  const offset = KITCHEN_SEATING_OVERHANG + KITCHEN_SEATING_CLEARANCE / 2;
  const center = {
    x: faceCenter.x + offset * outward.x,
    z: faceCenter.z + offset * outward.z,
  };
  // SAT-footprint convention (local +x → (cos ry, sin ry)) — the same
  // one `islandWallClearance` builds its obstacle rects in, NOT the
  // Three.js object convention used for emitted `rotation.y`.
  const ry = Math.atan2(along.z, along.x);
  return calculateCorners(center, length, KITCHEN_SEATING_CLEARANCE, ry);
}

/**
 * Footprint of the seating STRIP — the counter cantilever plus the
 * stools beneath it, i.e. everything the seating puts in the way.
 * Starts at the cabinet face (unlike the clearance zone, which starts at
 * the lip) and runs `SEATING_STRIP_DEPTH` outward.
 *
 * Same `edge` shape as `seatingZoneCorners`.
 */
function seatingStripCorners({ faceCenter, outward, along, length }) {
  const offset = SEATING_STRIP_DEPTH / 2;
  const center = {
    x: faceCenter.x + offset * outward.x,
    z: faceCenter.z + offset * outward.z,
  };
  const ry = Math.atan2(along.z, along.x);
  return calculateCorners(center, length, SEATING_STRIP_DEPTH, ry);
}

/**
 * True when seating this edge would put counter or stools inside a
 * walkway corridor — the gate for "the overhang must not overlap a
 * purple walkway".
 *
 * @param {Object} edge - As `seatingStripCorners`.
 * @param {Array<Array<{x,z}>>} [corridorRectangles=[]]
 */
function stripBlocksWalkway(edge, corridorRectangles = []) {
  return rectBlocksWalkway(seatingStripCorners(edge), corridorRectangles);
}

/**
 * True when an arbitrary footprint overlaps any walkway corridor. Shared
 * by the seating-strip gate and the peninsula overhang slab's
 * extend-to-the-corner test.
 */
function rectBlocksWalkway(corners, corridorRectangles = []) {
  if (!corners || !corridorRectangles?.length) return false;
  for (const rect of corridorRectangles) {
    if (rect && rect.length >= 3 && doPolygonsOverlap(corners, rect)) {
      return true;
    }
  }
  return false;
}

/**
 * Top-down footprint of a positioned object, or null when it has no
 * usable position / size. Same convention as the rest of the kitchen
 * SAT code: the object's Three.js `rotation.y` is fed straight to
 * `calculateCorners`.
 */
function footprintCorners(obj) {
  const c = obj?.center;
  const d = obj?.dimensions;
  if (!c || typeof c.x !== 'number' || typeof c.z !== 'number') return null;
  if (!(d?.x > 0) || !(d?.z > 0)) return null;
  return footprintFor({ x: c.x, z: c.z }, d.x, d.z, obj.rotation?.y ?? 0);
}

/** How far in from a zone corner the room-boundary test is made (1 mm). */
const ZONE_EDGE_TOLERANCE = 0.001;

/**
 * Test a seating zone against the space it would occupy. Used where the
 * caller has no free-depth measurement (peninsulas) or wants to confirm
 * an earlier, cabinet-blind proposal (islands enumerated before the
 * cabinet run is known).
 *
 * @param {Object} args
 * @param {{x,z}} args.faceCenter    - Midpoint of the cabinet face.
 * @param {{x,z}} args.outward       - Unit vector toward the seats.
 * @param {{x,z}} args.along         - Unit vector along the edge.
 * @param {number} args.length       - Edge length.
 * @param {Array<{x,z}>} [args.polygon] - Floor the zone must lie inside.
 *   Omit when the caller has already established containment.
 * @param {Array<Array<{x,z}>>} [args.footprints=[]] - Blocking footprints.
 * @returns {boolean}
 */
function probeSeatingZone({
  faceCenter,
  outward,
  along,
  length,
  polygon = null,
  footprints = [],
  band = null,
}) {
  const edge = { faceCenter, outward, along, length };
  const zone = band
    ? bandCorners(edge, band.from, band.to)
    : seatingZoneCorners(edge);

  if (polygon) {
    if (polygon.length < 3) return false;
    // A zone corner on the room's boundary is INSIDE the room: the arm's
    // end lies on the polygon's edge, so the zone's corner there is a coin
    // flip on float noise (a run whose first box projects a femtometre past
    // the arm's end read as outside, and lost its seating, 2026-09-14). Each
    // corner is tested a millimetre in toward the zone's centre.
    const cx = zone.reduce((a, c) => a + c.x, 0) / zone.length;
    const cz = zone.reduce((a, c) => a + c.z, 0) / zone.length;
    for (const corner of zone) {
      const dx = cx - corner.x;
      const dz = cz - corner.z;
      const d = Math.hypot(dx, dz) || 1;
      const probe = {
        x: corner.x + (dx / d) * ZONE_EDGE_TOLERANCE,
        z: corner.z + (dz / d) * ZONE_EDGE_TOLERANCE,
      };
      if (!isPointInsidePolygon(probe, polygon)) return false;
    }
  }
  for (const fp of footprints) {
    if (fp && fp.length >= 3 && doPolygonsOverlap(zone, fp)) return false;
  }
  return true;
}

/**
 * Build the stools for one seated edge.
 *
 * Stools stand on the floor in front of the counter, tucked
 * `STOOL_TUCK_UNDER` under the lip, evenly distributed along the edge,
 * each facing the counter (local +Z toward it, so the renderer's
 * backrest lands on the outside).
 *
 * @param {Object} args
 * @param {{x,z}} args.faceCenter - Midpoint of the cabinet face.
 * @param {{x,z}} args.outward    - Unit vector toward the seats.
 * @param {{x,z}} args.along      - Unit vector along the edge.
 * @param {number} args.length    - Edge length (drives the seat count).
 * @param {number} args.floorY
 * @param {string} args.idPrefix  - Disambiguates ids across edges.
 * @param {string} [args.fixtureGroupId] - Group stools with their counter.
 * @param {string} [args.label='Barstool']
 * @param {number} [args.overhang=KITCHEN_SEATING_OVERHANG] - The cantilever
 *   the stool tucks under. The generator's edges always get the standard 12";
 *   a hand-edited island gets the knee its own depth left, which the user
 *   chose and which may be less. The tuck follows it rather than being tuned
 *   apart from it, exactly as `STOOL_TUCK_UNDER` follows the constant.
 * @param {string} [args.addedBy='kitchen-layout'] - Provenance. A stool a
 *   CABINET RUN emits is owned by that run and swept with it, so it must not
 *   claim to be the layout generator's.
 * @returns {Array<Object>} Stool objects (empty when the edge is too short).
 */
function buildBarstoolObjects({
  faceCenter,
  outward,
  along,
  length,
  floorY,
  idPrefix,
  fixtureGroupId,
  label = 'Barstool',
  overhang = KITCHEN_SEATING_OVERHANG,
  addedBy = 'kitchen-layout',
}) {
  if (!edgeSeatsEnough(length)) return [];
  const seats = seatCountForEdge(length);

  const { width, depth, height } = BARSTOOL_DIMENSIONS;
  // Distance from the cabinet face out to the stool's center: past the
  // lip, then back in by the tuck.
  const tuck = overhang / 2;
  const perp = overhang + depth / 2 - tuck;
  // Local +Z faces the counter, i.e. −outward.
  const ry = Math.atan2(-outward.x, -outward.z);
  const now = new Date().toISOString();

  const stools = [];
  for (let i = 0; i < seats; i += 1) {
    const alongOffset = -length / 2 + (length * (i + 0.5)) / seats;
    stools.push({
      id: `${idPrefix}_barstool_${i}`,
      type: 'bar-stool',
      label,
      category: { 'bar-stool': true, seating: true },
      center: {
        x: faceCenter.x + alongOffset * along.x + perp * outward.x,
        y: floorY + height / 2,
        z: faceCenter.z + alongOffset * along.z + perp * outward.z,
      },
      dimensions: { x: width, y: height, z: depth },
      rotation: { x: 0, y: ry, z: 0 },
      ...(fixtureGroupId ? { fixtureGroupId } : {}),
      addedBy,
      lastModified: now,
    });
  }
  return stools;
}

/**
 * Resolve an island's `candidate.seating` into the pieces both island
 * builders need. `seating.facing` is a world direction (the same
 * encoding `sinkSlot.facing` uses), so it is converted into the
 * island's local frame here rather than in each builder.
 *
 * On a shallow island the seated edge trades its 1" reveal for the 12"
 * knee overhang, so the slab grows by the difference on that side only and
 * its center shifts by half that. On a deep island the knee is carved from
 * the depth instead (`kneeDepth` > 0): the slab keeps its reveal and the
 * seated edge moves in by the knee.
 *
 * @param {Object|null|undefined} seating - `candidate.seating`.
 * @param {{x,z}} axis2 - Island-local depth axis, in world coordinates.
 * @param {{kneeDepth?: number}} [opts] - `frontBackDepths(...).kneeDepth`.
 * @returns {{seatSign: number, extraDepth: number, centerShift: number, faceInset: number} | null}
 */
function islandSeatingGeometry(seating, axis2, { kneeDepth = 0 } = {}) {
  const facing = seating?.facing;
  if (!facing) return null;
  const seatSign = facing.x * axis2.x + facing.z * axis2.z >= 0 ? 1 : -1;
  // Where the knee space is decides everything else. A deep island CARVES
  // it from its own depth (`frontBackDepths`: the back row shrinks by
  // `kneeDepth`), so the counter edge stays at the body face and the stools
  // pull in by the knee to sit under it. A shallow island has nothing to
  // carve and cantilevers the slab past the face instead; the stools stand
  // at the body face and the slab reaches over them. Both builders used to
  // mix the two: the bare builder carved the knee but placed the stools
  // for a cantilever (stools 14" off a 42" island's counter, 819 Montrose
  // card #28), and the sink builder carved AND cantilevered — 24" of knee.
  const carved = kneeDepth > 0;
  const extraDepth = carved
    ? 0
    : KITCHEN_SEATING_OVERHANG - KITCHEN_COUNTERTOP_FRONT_OVERHANG;
  return {
    seatSign,
    extraDepth,
    centerShift: (seatSign * extraDepth) / 2,
    // How far in from the body face the seated edge (the stools' reference
    // face) sits. Carved: the knee less the slab's reveal, so a stool stands
    // the same distance under the counter EDGE as under a cantilever — the
    // diner's knee room is what is being kept equal, and the carved slab
    // still overhangs the body by its reveal. Cantilevered: nothing.
    faceInset: carved ? kneeDepth - KITCHEN_COUNTERTOP_FRONT_OVERHANG : 0,
  };
}

/**
 * Re-route the drawn walkway rectangles clear of the seating.
 *
 * `seatingCirculationOk` lets a seated edge sit in a corridor as long as
 * the aisle can carry the stools PLUS a full walkway behind them — the
 * corridor rectangle is one instantiation of a path, free to move. But
 * the preview then draws a path running through the stools, which reads
 * as a bug. This re-instantiates it: any rectangle overlapping a stool
 * is translated along that stool's outward direction until it clears,
 * keeping its full width (which is exactly what the depth gate reserved
 * room for).
 *
 * Purely presentational — it never changes what was allowed, only where
 * the path is drawn. Rectangles that touch no stool pass through
 * untouched.
 *
 * @param {Array<Array<{x,z}>>} corridorRectangles
 * @param {Array<Object>} objects - Materialized objects; stools are picked out.
 * @returns {Array<Array<{x,z}>>}
 */
function routeCorridorsClearOfSeating(corridorRectangles, objects) {
  const stools = (objects || []).filter((o) => o?.type === 'bar-stool');
  if (!stools.length || !corridorRectangles?.length) {
    return corridorRectangles || [];
  }
  const seats = stools
    .map((s) => {
      const corners = footprintCorners(s);
      if (!corners) return null;
      const ry = s.rotation?.y ?? 0;
      // Stools face the counter along local +Z, so they sit outboard
      // along the opposite direction.
      return { corners, outward: { x: -Math.sin(ry), z: -Math.cos(ry) } };
    })
    .filter(Boolean);

  const proj = (pts, dir) => pts.map((p) => p.x * dir.x + p.z * dir.z);

  return corridorRectangles.map((rect) => {
    if (!rect || rect.length < 3) return rect;
    let push = 0;
    let dir = null;
    for (const seat of seats) {
      if (!doPolygonsOverlap(rect, seat.corners)) continue;
      const seatFar = Math.max(...proj(seat.corners, seat.outward));
      const rectNear = Math.min(...proj(rect, seat.outward));
      const need = seatFar - rectNear;
      if (need > push) {
        push = need;
        dir = seat.outward;
      }
    }
    if (!(push > 0) || !dir) return rect;
    return rect.map((p) => ({ x: p.x + push * dir.x, z: p.z + push * dir.z }));
  });
}

/**
 * Y of the seated countertop's top surface — the reference a stool's
 * seat height is chosen against. Exported so tests can assert the
 * counter/seat relationship rather than restating both numbers.
 */
function seatedCounterTopY(floorY) {
  return (
    floorY + KITCHEN_CABINET_DIMENSIONS.height + KITCHEN_COUNTERTOP_THICKNESS
  );
}

module.exports = {
  seatedIslandFreeDepthNeeded,
  SEATING_DEPTH_REQUIRED,
  SEATING_STRIP_DEPTH,
  SEATING_PLUS_WALKWAY_DEPTH,
  STOOL_TUCK_UNDER,
  seatingFitsInDepth,
  seatCountForEdge,
  edgeSeatsEnough,
  seatingZoneCorners,
  bandCorners,
  requiredClearBand,
  seatingStripCorners,
  stripBlocksWalkway,
  rectBlocksWalkway,
  seatingCirculationOk,
  probeSeatingZone,
  footprintCorners,
  islandSeatingGeometry,
  buildBarstoolObjects,
  routeCorridorsClearOfSeating,
  seatedCounterTopY,
};
